import type { SvgIconComponent } from '@mui/icons-material';
import AllTasksIcon from '@mui/icons-material/ChecklistRtl';
import ProjectDashboardIcon from '@mui/icons-material/SpaceDashboardOutlined';
import ProjectTasksIcon from '@mui/icons-material/ManageHistoryOutlined';
import PeopleIcon from '@mui/icons-material/GroupsOutlined';
import PortfolioIcon from '@mui/icons-material/WorkspacesOutlined';
import ScheduleIcon from '@mui/icons-material/ViewTimelineOutlined';
import SentMessagesIcon from '@mui/icons-material/SmsOutlined';
import WorkloadIcon from '@mui/icons-material/SpeedOutlined';

/**
 * A place in the navigation.
 *
 * @remarks
 * `roles` hides what a person cannot use; it does not secure anything. The server
 * re-authorizes every request, so a hidden route is a tidier interface rather than a
 * protected one.
 */
export interface NavItem {
  to: string;
  label: string;
  roles?: readonly string[];

  /** Further pages this entry stands for, so it stays marked while one of them is open. */
  covers?: readonly string[];
}

/** A report: a page offered as a tile on the reports page as well as in the navigation. */
export interface Report extends NavItem {
  description: string;
  icon: SvgIconComponent;

  /** The palette colour the tile is drawn in. Decoration only - it says nothing about state. */
  tone: 'primary' | 'secondary' | 'info' | 'success' | 'warning';
}

/**
 * The role whose reports are gathered behind one entry.
 *
 * @remarks
 * A system administrator is offered every report, and seven of them beside the pages people
 * work in made the navigation too long to scan. Every other role is offered at most four
 * and keeps them where they were.
 */
const GATHERS_REPORTS = 'SystemAdministrator';

const SCHEDULE: Report = {
  to: '/schedule',
  label: 'برنامه زمانی',
  description: 'زمان‌بندی کارهای پروژه، وابستگی‌ها و مسیر بحرانی',
  icon: ScheduleIcon,
  tone: 'info',
};

const WORKLOAD: Report = {
  to: '/workload',
  label: 'بار کاری',
  description: 'کار برنامه‌ریزی‌شدهٔ هر نفر در برابر ظرفیتش، هفته به هفته',
  icon: WorkloadIcon,
  tone: 'warning',

  // The same fields as the roster below, a week at a time: every colleague's contracted
  // hours, role and team. A planning role rather than an administrator one, because the
  // grid is scoped to the projects the caller can already see and planning one of them is
  // what it is for. The server refuses as well - this only stops offering a page that
  // would answer 403.
  roles: ['ProjectManager', 'SystemAdministrator'],
};

const PEOPLE: Report = {
  to: '/people',
  label: 'افراد',
  description: 'همکاران با ساعت کاری، نقش و مرخصی هر نفر',
  icon: PeopleIcon,
  tone: 'success',

  // The roster is every colleague's contracted hours, role and leave in one list. That is a
  // picture of the organization rather than of a project, so it is an administrator's view.
  roles: ['SystemAdministrator'],
};

const PROJECT_DASHBOARD: Report = {
  to: '/project-dashboard',
  label: 'داشبورد پروژه',
  description: 'وضعیت و پیشرفت یک پروژه در یک نگاه',
  icon: ProjectDashboardIcon,
  tone: 'primary',
};

const PORTFOLIO: Report = {
  to: '/portfolio',
  label: 'پرتفوی',
  description: 'سلامت همهٔ پروژه‌ها در کنار هم',
  icon: PortfolioIcon,
  tone: 'secondary',
};

const ALL_TASKS: Report = {
  to: '/admin/tasks',
  label: 'همه کارها',
  description: 'کارهای همهٔ پروژه‌ها در یک فهرست، با جستجو و فیلتر',
  icon: AllTasksIcon,
  tone: 'info',

  // Every task in every project on one page. That is a picture of the organization rather
  // than of a project - and the only page here with no project selector, because not
  // choosing one is the point. The server refuses anybody else, and it still scopes the
  // rows to the projects this person can see in GitLab.
  roles: ['SystemAdministrator'],
};

const PROJECT_TASKS: Report = {
  to: '/admin/project-tasks',
  label: 'کارهای پروژه‌ها',
  description: 'ایجادکننده، مسئول و آخرین تغییر هر کار، با جزئیات کامل زیر هر ردیف',
  icon: ProjectTasksIcon,
  tone: 'primary',

  // Like the all-tasks page, a picture of the organization rather than of a project. The
  // server refuses anybody else and still scopes the rows to this person's GitLab projects.
  roles: ['SystemAdministrator'],
};

const SENT_MESSAGES: Report = {
  to: '/admin/notifications',
  label: 'پیام‌های ارسالی',
  description: 'پیامک‌هایی که سامانه برای همکاران فرستاده و وضعیت هر کدام',
  icon: SentMessagesIcon,
  tone: 'secondary',

  // What the product told people, and what became of each message. An organization-wide
  // record naming colleagues and the times they were contacted, so an administrator's
  // view - and still scoped on the server to the projects this person can see in GitLab.
  roles: ['SystemAdministrator'],
};

/** The tiles, in the order the product owner listed them; the project tasks beside all tasks. */
const REPORTS: readonly Report[] = [
  ALL_TASKS,
  PROJECT_TASKS,
  SENT_MESSAGES,
  PEOPLE,
  WORKLOAD,
  SCHEDULE,
  PROJECT_DASHBOARD,
  PORTFOLIO,
];

/** The one entry a system administrator is offered in place of the reports. */
export const REPORTS_ENTRY: NavItem = {
  to: '/reports',
  label: 'گزارشات',
  roles: [GATHERS_REPORTS],
  covers: REPORTS.map((report) => report.to),
};

/** Every place, in the order the navigation has always shown them. */
const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: 'کارهای من' },
  { to: '/board', label: 'تخته کار' },
  { to: '/projects', label: 'پروژه‌ها' },
  { to: '/phases', label: 'فازها' },
  SCHEDULE,
  WORKLOAD,
  PEOPLE,
  PROJECT_DASHBOARD,
  PORTFOLIO,
  ALL_TASKS,
  PROJECT_TASKS,
  SENT_MESSAGES,
  { to: '/admin/sync', label: 'سلامت همگام‌سازی', roles: ['SystemAdministrator'] },
];

function isOffered(item: NavItem, role: string | undefined): boolean {
  return !item.roles || (role !== undefined && item.roles.includes(role));
}

function isReport(item: NavItem): boolean {
  return REPORTS.some((report) => report.to === item.to);
}

/**
 * What the navigation offers a role.
 *
 * @remarks
 * A system administrator gets one entry where the first report was, and the reports behind
 * it. Everybody else gets exactly what they had before the reports were gathered.
 */
export function navigationFor(role: string | undefined): NavItem[] {
  const offered = NAV_ITEMS.filter((item) => isOffered(item, role));

  if (role !== GATHERS_REPORTS) {
    return offered;
  }

  const firstReport = offered.findIndex(isReport);
  const others = offered.filter((item) => !isReport(item));

  // Nothing before the first report is one, so its position is the same in both lists.
  others.splice(firstReport, 0, REPORTS_ENTRY);

  return others;
}

/** The reports a role is offered, for the reports page. */
export function reportsFor(role: string | undefined): Report[] {
  return REPORTS.filter((report) => isOffered(report, role));
}

/** Whether an entry is the page now open, or stands for it. */
export function isCurrent(item: NavItem, pathname: string): boolean {
  if (item.to === '/') {
    return pathname === '/';
  }

  return [item.to, ...(item.covers ?? [])].some((path) => pathname.startsWith(path));
}
